import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Alert,
  AlertTitle,
  Chip,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
} from "@mui/material";
import {
  CheckCircle,
  Warning,
  Error,
  Info,
  Cancel,
  ThumbDown,
} from "@mui/icons-material";
import SectionLabel from "../../widgets/SectionLabel";

const WhatsAppTemplateValidator = ({ template }) => {
  const [qualityExpanded, setQualityExpanded] = useState(false);
  const [optOutExpanded, setOptOutExpanded] = useState(false);

  // Check if template is marketing
  const isMarketing = template?.category === "MARKETING";

  // Keywords to check for opt-out (case insensitive)
  const optOutKeywords = [
    "stop",
    "unsubscribe",
    "stop promotion",
    "block this number",
    "do not",
    "Not interested",
    "opt out",
    "do not disturb",
  ];

  // Function to check if any button contains opt-out keywords
  const checkOptOutButton = () => {
    if (!template?.components) return { found: false, buttons: [] };

    const allButtons = [];

    // Check regular buttons
    template.components.forEach((component) => {
      if (component.type === "BUTTONS" && component.buttons) {
        component.buttons.forEach((button) => {
          if (button.type === "QUICK_REPLY" && button.text) {
            allButtons.push(button.text);
          }
        });
      }

      // Check carousel cards
      if (component.type === "CAROUSEL" && component.cards) {
        component.cards.forEach((card) => {
          card.components?.forEach((cardComponent) => {
            if (cardComponent.type === "BUTTONS" && cardComponent.buttons) {
              cardComponent.buttons.forEach((button) => {
                if (button.type === "QUICK_REPLY" && button.text) {
                  allButtons.push(button.text);
                }
              });
            }
          });
        });
      }
    });

    // Check if any button matches opt-out keywords
    const hasOptOut = allButtons.some((buttonText) =>
      optOutKeywords.some((keyword) =>
        buttonText.toLowerCase().includes(keyword.toLowerCase())
      )
    );

    return { found: hasOptOut, buttons: allButtons };
  };

  // Check quality score
  const getQualityStatus = () => {
    const score = template?.quality_score?.score;

    if (!score || score === "UNKNOWN") {
      return {
        status: "unknown",
        severity: "info",
        message: "Quality score not yet determined",
      };
    }

    if (score === "GREEN" || score === "HIGH") {
      return {
        status: "good",
        severity: "success",
        message: "Good quality template",
      };
    }

    if (score === "YELLOW" || score === "MEDIUM") {
      return {
        status: "medium",
        severity: "warning",
        message: "Medium quality - monitor performance",
      };
    }

    if (score === "RED" || score === "LOW" || score === "POOR") {
      return {
        status: "poor",
        severity: "error",
        message: "Poor quality - template may be flagged",
      };
    }

    return {
      status: "unknown",
      severity: "info",
      message: "Quality score: " + score,
    };
  };

  if (!template) {
    return (
      <Card sx={{ maxWidth: 800, margin: "auto", mt: 4 }}>
        <CardContent>
          <Alert severity="info">
            <AlertTitle>No Template Provided</AlertTitle>
            Please provide a WhatsApp template to validate.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!isMarketing) {
    return (
      <Card sx={{ maxWidth: 800, margin: "auto", mt: 4 }}>
        <CardContent>
          <Alert severity="info" icon={<Info />}>
            <AlertTitle>Non-Marketing Template</AlertTitle>
            This template is not a MARKETING template (Category:{" "}
            {template.category}). Opt-out validation only applies to marketing
            messages.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const optOutCheck = checkOptOutButton();
  const qualityStatus = getQualityStatus();

  return (
    <Card sx={{ maxWidth: 800, margin: "auto", mt: 4 }}>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Template Validator Check
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Chip
              label={`Category: ${template.category}`}
              color="primary"
              size="small"
            />
            <Chip
              label={`Status: ${template.status}`}
              color={template.status === "APPROVED" ? "success" : "warning"}
              size="small"
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Quality Score Check */}
        <Box sx={{ mb: 3 }}>
          <SectionLabel mb={2}>Quality Score</SectionLabel>
          <Alert
            severity={qualityStatus.severity}
            icon={
              qualityStatus.severity === "error" ? <ThumbDown /> : undefined
            }
          >
            <AlertTitle>{qualityStatus.message}</AlertTitle>
            {qualityStatus.status === "poor" && (
              <Box sx={{ mt: 1 }}>
                {!qualityExpanded && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">
                      Poor quality templates can lead to higher block rates and
                      restrictions.
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setQualityExpanded(true)}
                      variant="text"
                    >
                      Learn more
                    </Button>
                  </Box>
                )}
                {qualityExpanded && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Warning:</strong> Poor quality templates can lead
                      to:
                    </Typography>
                    <List dense>
                      <ListItem sx={{ py: 0 }}>
                        <ListItemText
                          primary="• Higher block rates by users"
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0 }}>
                        <ListItemText
                          primary="• Reduced message delivery rates"
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0 }}>
                        <ListItemText
                          primary="• Potential account restrictions"
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0 }}>
                        <ListItemText
                          primary="• Damage to brand reputation"
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    </List>
                    <Button
                      size="small"
                      onClick={() => setQualityExpanded(false)}
                      variant="text"
                    >
                      Show less
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Alert>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Opt-Out Button Check */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Opt-Out Compliance
          </Typography>

          {optOutCheck.found ? (
            <Alert severity="success" icon={<CheckCircle />}>
              <AlertTitle>✓ Opt-Out Button Found</AlertTitle>
              Your template includes an opt-out option. This is compliant with
              best practices.
            </Alert>
          ) : (
            <Alert severity="error" icon={<Cancel />}>
              <AlertTitle>✗ No Opt-Out Button Detected</AlertTitle>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Your marketing template is missing an opt-out button
                (Stop/Unsubscribe).
              </Typography>
              {!optOutExpanded && (
                <Button
                  size="small"
                  onClick={() => setOptOutExpanded(true)}
                  variant="text"
                >
                  Learn more
                </Button>
              )}
              {optOutExpanded && (
                <>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Why this is critical:</strong>
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Warning color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Legal Compliance"
                          secondary="Many countries require opt-out options for marketing messages (GDPR, TCPA, etc.)"
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: 600,
                          }}
                          secondaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Warning color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="WhatsApp Policy"
                          secondary="WhatsApp requires marketing messages to include a way to opt-out"
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: 600,
                          }}
                          secondaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Warning color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Reduced Block Rate"
                          secondary="Users who can easily unsubscribe are less likely to block your number"
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: 600,
                          }}
                          secondaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Warning color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Better Quality Score"
                          secondary="Templates with opt-out options typically maintain higher quality ratings"
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: 600,
                          }}
                          secondaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Warning color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="User Trust"
                          secondary="Giving users control builds trust and improves brand perception"
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: 600,
                          }}
                          secondaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    </List>
                  </Box>

                  <Box
                    sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Recommendation:</strong>
                    </Typography>
                    <Typography variant="body2">
                      Add a Quick Reply button with text like "Stop",
                      "Unsubscribe", or "Stop Promotions" to allow users to
                      easily opt-out of future marketing messages.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => setOptOutExpanded(false)}
                    variant="text"
                    sx={{ mt: 1 }}
                  >
                    Show less
                  </Button>
                </>
              )}
            </Alert>
          )}

          {optOutCheck.buttons.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Quick Reply buttons found:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {optOutCheck.buttons.map((buttonText, index) => (
                  <Chip
                    key={index}
                    label={buttonText}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
export default WhatsAppTemplateValidator;
